import {useEffect} from 'react';
import {Form, Row} from 'react-bootstrap';
import {FormikProps} from 'formik';
import {useIntl} from 'react-intl';
import {CustomerScope, LoginAsCustomerFormValues} from '../../../interfaces/login';
import AutocompleteBu from './AutocompleteComponent';
import CustomerSelector from './CustomerSelector';
import {
  useCurrentCustomer,
  usePermissions,
} from '../../../modules/auth/authorization/PermissionProvider';
import {checkIfAuthorized} from '../../../modules/auth/authorization/api';
import {BuOption} from '../../../types/Customer';
import {useCustomerList} from '../../../hooks/useCustomerManagerHook';

interface LoginAsCustomerFormProps {
  config: FormikProps<LoginAsCustomerFormValues>;
}

const LoginAsCustomerForm = ({config}: LoginAsCustomerFormProps): JSX.Element => {
  const intl = useIntl();
  const currentCustomer = useCurrentCustomer();
  const userPermissions = usePermissions();

  const {data: customers = []} = useCustomerList();

  const isAdmin = checkIfAuthorized(userPermissions, ['ecAdmin']);
  const isOneCustomer = config.values.scope === 'one';

  const handleAutocompleteChange = (value: BuOption) => {
    config.setValues({
      ...config.values,
      system: value.system,
      client_id: value.id.toString(),
    });
  };

  const handleScopeChange = (scope: CustomerScope) => {
    config.setFieldValue('scope', scope);
  };

  // Un seul client possible : il est sélectionné d'office
  useEffect(() => {
    if (!isAdmin && customers.length === 1 && config.values.client_id === '') {
      handleAutocompleteChange(customers[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, customers]);

  // Le compteur n'a pas de sens pour un ecAdmin, qui peut choisir n'importe quel client
  const allCustomersLabel = isAdmin
    ? intl.formatMessage({id: 'CUSTOMER_SCOPE.ALL_CUSTOMERS'})
    : intl.formatMessage({id: 'CUSTOMER_SCOPE.ALL_CUSTOMERS_COUNT'}, {count: customers.length});

  return (
    <Form onSubmit={config.handleSubmit}>
      <Form.Check
        type="radio"
        id="customer-scope-all"
        name="scope"
        className="mb-5"
        checked={!isOneCustomer}
        onChange={() => handleScopeChange('all')}
        label={
          <>
            <span className="fw-bold">{allCustomersLabel}</span>
            <div className="text-muted fs-7">
              {intl.formatMessage({id: 'CUSTOMER_SCOPE.ALL_HELP'})}
            </div>
          </>
        }
      />
      <Form.Check
        type="radio"
        id="customer-scope-one"
        name="scope"
        checked={isOneCustomer}
        onChange={() => handleScopeChange('one')}
        label={
          <>
            <span className="fw-bold">
              {intl.formatMessage({id: 'CUSTOMER_SCOPE.ONE_CUSTOMER'})}
            </span>
            <div className="text-muted fs-7">
              {intl.formatMessage({id: 'CUSTOMER_SCOPE.ONE_HELP'})}
            </div>
          </>
        }
      />
      {isOneCustomer && (
        <Row className="mt-3 mb-5 ms-6">
          {isAdmin ? (
            <AutocompleteBu
              name="client_id"
              defaultValue={currentCustomer?.id.toString() ?? ''}
              onChange={handleAutocompleteChange}
              label={intl.formatMessage({id: 'CUSTOMER_SCOPE.FIELD'})}
              error={config.errors['client_id']}
              touched={config.touched['client_id']}
              placeholder={intl.formatMessage({id: 'CUSTOMER_SCOPE.SEARCH'})}
            />
          ) : (
            <CustomerSelector
              name="client_id"
              defaultValue={currentCustomer?.id.toString() ?? ''}
              currentCustomerId={currentCustomer?.id.toString()}
              value={config.values.client_id}
              onChange={handleAutocompleteChange}
              label={intl.formatMessage({id: 'CUSTOMER_SCOPE.FIELD'})}
              error={config.errors['client_id']}
              touched={config.touched['client_id']}
              placeholder={intl.formatMessage({id: 'CUSTOMER_SCOPE.SEARCH'})}
            />
          )}
        </Row>
      )}
    </Form>
  );
};

export default LoginAsCustomerForm;
